import axios from 'axios'
import type {
  CreateRealTimeCurtailmentDecisionAttributesRequest,
  RealTimeCurtailmentDecisionAttributes,
  AutoCurtailmentFormState,
  UpdateRealTimeCurtailmentDecisionAttributesRequest,
} from '@/types/eos/AutoCurtailmentManagement'

const API_ENDPOINT = (siteId: string) =>
  `${import.meta.env.VITE_APP_SITES_ENDPOINT}/${siteId}/real-time-curtailment-decision-attributes`

/**
 * Transform form state to API request
 */
const transformFormToRequest = (
  form: AutoCurtailmentFormState
): CreateRealTimeCurtailmentDecisionAttributesRequest | UpdateRealTimeCurtailmentDecisionAttributesRequest => {
  if (form.curtail_on_custom_time_window) {
    return {
      ...form,
      custom_curtailment_window_start_hour: form.custom_curtailment_window_start_hour
        ? parseInt(form.custom_curtailment_window_start_hour)
        : null,
      custom_curtailment_window_end_hour: form.custom_curtailment_window_end_hour
        ? parseInt(form.custom_curtailment_window_end_hour)
        : null,
    } as CreateRealTimeCurtailmentDecisionAttributesRequest | UpdateRealTimeCurtailmentDecisionAttributesRequest
  }

  return {
    ...form,
    custom_curtailment_window_start_hour: null,
    custom_curtailment_window_end_hour: null,
  } as unknown as
    CreateRealTimeCurtailmentDecisionAttributesRequest | UpdateRealTimeCurtailmentDecisionAttributesRequest
}

export const autoCurtailmentManagementService = {
  /**
   * Get real time curtailment decision attributes for a site
   * @param siteId - The site ID
   * @returns The real time curtailment decision attributes
   */
  getRealTimeCurtailmentDecisionAttributes: async (siteId: string): Promise<RealTimeCurtailmentDecisionAttributes> => {
    const { data } = await axios.get<RealTimeCurtailmentDecisionAttributes>(API_ENDPOINT(siteId))
    return data
  },

  /**
   * Create real time curtailment decision attributes for a site
   * @param siteId - The site ID
   * @param form - The form state to create from
   * @returns The created real time curtailment decision attributes
   */
  createRealTimeCurtailmentDecisionAttributes: async (
    siteId: string,
    form: AutoCurtailmentFormState
  ): Promise<RealTimeCurtailmentDecisionAttributes> => {
    const request = transformFormToRequest(form)
    const { data } = await axios.post<RealTimeCurtailmentDecisionAttributes>(API_ENDPOINT(siteId), request)
    return data
  },

  /**
   * Update real time curtailment decision attributes for a site
   * @param siteId - The site ID
   * @param form - The form state to update from
   * @returns The updated real time curtailment decision attributes
   */
  updateRealTimeCurtailmentDecisionAttributes: async (
    siteId: string,
    form: AutoCurtailmentFormState
  ): Promise<RealTimeCurtailmentDecisionAttributes> => {
    const request = transformFormToRequest(form)
    const { data } = await axios.put<RealTimeCurtailmentDecisionAttributes>(API_ENDPOINT(siteId), request)
    return data
  },

  /**
   * Delete real time curtailment decision attributes for a site
   * @param siteId - The site ID
   */
  deleteRealTimeCurtailmentDecisionAttributes: async (siteId: string): Promise<void> => {
    await axios.delete(API_ENDPOINT(siteId))
  },
}
